'use client';

import {
  AppEnv,
  getLoggedInUserHomePagePathIfAny,
  headerHeight,
  AuthStatus,
  adminAndIndependentRoles,
  hasFrontendAccessWithRoleForOrganization
} from '@efacity/common';
import type { OrganizationInformation } from '@efacity/react-next-sc';
import { BranchTooltip, useAuth } from '@efacity/react-next-sc';
import { PATHS, toPath } from '@efacity/routing';
import { AppBar } from '@mui/material';
import HeaderLogo from './HeaderLogo';
import { HeaderOrgName } from './HeaderOrgName';
import { HeaderShoppingCart } from './HeaderShoppingCart';
import HeaderUserMenu from './HeaderUserMenu';
import SignInButtons from './SignInButtons';
import type { ReactNode } from 'react';
import { HeaderLeftMenuToggleBtn } from './HeaderLeftMenuToggleBtn';
import { useEffect } from 'react';

const NEXT_PUBLIC_APP_ENV = process.env['NEXT_PUBLIC_APP_ENV'];
const APP_ENV = process.env['APP_ENV'];
const BRANCH_NAME = process.env['NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF'] || process.env['REACT_APP_VERCEL_GIT_COMMIT_REF'];

interface HeaderProps {
  orgInfo?: OrganizationInformation;
  rightSectionItems?: ReactNode;
  allowLogoutRedirect?: boolean;
  showLeftNavigationButton?: boolean;
  allowCustomerHomePageRedirect?: boolean;
}

export const Header = ({
  orgInfo,
  rightSectionItems,
  allowLogoutRedirect = true,
  showLeftNavigationButton = false,
  allowCustomerHomePageRedirect = false
}: HeaderProps) => {
  // uncomment next line to allow header to scroll out of view in small screen modes
  // const { scrollDirection } = useVerticalScroll();

  const {
    authState: { isAuthenticated, extraAuthInfo, user, workingAccessId },
    getAllowedCreateActivity
  } = useAuth();

  const isUserLoggedIn = isAuthenticated === AuthStatus.Authenticated;
  const startRegistrationURL = extraAuthInfo?.startRegistrationURL;
  const logoRedirectPath =
    allowCustomerHomePageRedirect && user?.access
      ? getLoggedInUserHomePagePathIfAny(user.access, '', startRegistrationURL)
      : orgInfo?._id &&
        hasFrontendAccessWithRoleForOrganization(orgInfo._id, adminAndIndependentRoles, user?.adminAccesses || [])
      ? toPath(PATHS.orgLanding, { orgId: orgInfo._id })
      : startRegistrationURL
      ? startRegistrationURL
      : PATHS.landing;

  const appEnv = (NEXT_PUBLIC_APP_ENV || APP_ENV) as AppEnv;

  // there are several places where we need to get the allowed create activity
  // so leave it here
  useEffect(() => {
    if (workingAccessId) {
      getAllowedCreateActivity(workingAccessId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingAccessId]);

  return (
    <AppBar
      color="default"
      sx={{
        height: `${headerHeight}px`,
        left: 0,
        right: 0,
        top: 0,
        position: 'sticky',
        overflowX: 'hidden',
        transition: 'transform 0.5s ease-in-out 0s',
        paddingTop: '5px',
        paddingBottom: '5px',
        paddingLeft: '10px',
        paddingRight: '10px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'hidden'
        // uncomment next line to allow header to scroll out of view in small screen modes
        // transform: scrollDirection === 'down' ? { xs: 'translateY(-100%)', md: 'none' } : 'none'
      }}
      data-testid="main-layout-header"
    >
      {showLeftNavigationButton && <HeaderLeftMenuToggleBtn />}
      <HeaderLogo
        orgName={orgInfo?.name}
        orgLogoDesktop={orgInfo?.logoFullUrl}
        orgLogoMobile={orgInfo?.mobileLogoFullUrl}
        logoRedirectPath={logoRedirectPath}
      />
      {appEnv !== AppEnv.PROD && <BranchTooltip branchName={BRANCH_NAME} />}
      <HeaderOrgName appEnv={appEnv} isUserLoggedIn={isUserLoggedIn} orgName={orgInfo?.name} />
      {rightSectionItems}
      <HeaderShoppingCart />
      {isUserLoggedIn ? <HeaderUserMenu allowLogoutRedirect={allowLogoutRedirect} /> : <SignInButtons />}
    </AppBar>
  );
};
